import {
    useCreatePortfolioActions,
    useCreatePortfolioSelectors,
    useCreatePortfolioState,
} from 'datasource/CreatePortfolio';
import moment from 'moment';
import {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import SM from 'services/ServiceManager';
import { notification } from 'ui-library';
import { FEE_TYPES } from 'datasource/CreatePortfolio/constants';
import { STRATEGY_CHANGED } from 'constants/sessionStorageKeys';
import distinguishClientType from './distinguishClientType';

type UseCustodyFeeParams = {
    dfsClientId?: unknown;
    isZivAzp?: boolean;
    isZic?: boolean;
    isAzp?: boolean;
};

export const useCustodyFee = ({
    dfsClientId, isZivAzp, isZic, isAzp,
}: UseCustodyFeeParams) => {
    const {
        currentInvestmentApplicationSelector,
        currentInvestmentDescriptionSelector,
        jointClientSelector,
        is3aPremiumProductGroupSelector,
    } = useCreatePortfolioSelectors();
    const {
        currentInvestmentApplicationId: investmentApplicationId,
    } = useCreatePortfolioState();
    const {
        saveInvestmentApplicationDataAction,
        setDefaultAssetManagementFeeInCNP,
    } = useCreatePortfolioActions();

    const modelPortfolioId = useMemo(() => currentInvestmentDescriptionSelector?.selectStrategy?.modelPortfolioId, [currentInvestmentDescriptionSelector?.selectStrategy?.modelPortfolioId]);
    const investedAmount = useMemo(
        () => (
            isAzp
                ? currentInvestmentDescriptionSelector?.withdrawal?.investmentAmount
                : currentInvestmentDescriptionSelector?.productConfiguration?.initialInvestment),
        [
            currentInvestmentDescriptionSelector?.productConfiguration?.initialInvestment,
            isAzp,
            currentInvestmentDescriptionSelector?.withdrawal?.investmentAmount,
        ],
    );
    const clientType = useMemo(() => distinguishClientType(currentInvestmentDescriptionSelector?.personalDetails?.zurichEmployee || jointClientSelector?.isZurichEmployee, currentInvestmentDescriptionSelector?.personalDetails?.destinataryVita || jointClientSelector?.destinataryVita), [currentInvestmentDescriptionSelector?.personalDetails?.zurichEmployee, currentInvestmentDescriptionSelector?.personalDetails?.destinataryVita, jointClientSelector?.isZurichEmployee, jointClientSelector?.destinataryVita]);

    const [fees, setFees] = useState<Record<string, unknown>[]>([]);

    const defaultCustodyFee = useMemo(() => JSON.parse(currentInvestmentApplicationSelector?.modelPortfolio?.Attributes || '{}')?.custodyFee, [currentInvestmentApplicationSelector?.modelPortfolio?.Attributes]);
    const defaultEntryFee = useMemo(() => JSON.parse(currentInvestmentApplicationSelector?.modelPortfolio?.Attributes || '{}')?.entryCost, [currentInvestmentApplicationSelector?.modelPortfolio?.Attributes]);
    const defaultAdministrationFee = useMemo(() => JSON.parse(currentInvestmentApplicationSelector?.modelPortfolio?.Attributes || '{}')?.AdministrationFee, [currentInvestmentApplicationSelector?.modelPortfolio?.Attributes]);

    const [defaultAssetFee, setDefaultAssetFee] = useState(null);
    const [defaultAssetDiscount, setDefaultAssetDiscount] = useState(0); // used for vita and zurich customers

    useEffect(() => {
        const fetchAssetData = async () => {
            const payload = {
                modelPortfolioId,
                investedAmount: investedAmount ?? 0,
                clientType,
            };

            if (isZivAzp) {
                try {
                    const { data } = await SM.planService('getAssetManagement', [payload]);

                    setDefaultAssetFee(data?.standardFee);
                    setDefaultAssetDiscount(data?.discount || 0);
                } catch (error) {
                    // Handle error here
                    console.error(error);
                }
            }
        };

        fetchAssetData();
    }, [isZivAzp, modelPortfolioId, investedAmount, clientType]);

    const defaultFees = useMemo(() => {
        const tempData = [];

        if (defaultCustodyFee != null && !is3aPremiumProductGroupSelector) {
            tempData.push({ fee: defaultCustodyFee, feeType: FEE_TYPES.custody, startDate: moment() });
        }
        if (isZivAzp) {
            const tempFee = defaultAssetFee * (1 - defaultAssetDiscount / 100);

            tempData.push({
                fee: parseFloat(tempFee.toFixed(2).replace(/\.?0*$/, '')),
                discountValue: defaultAssetDiscount,
                feeType: FEE_TYPES.asset,
                startDate: moment(),
            });
        }
        if (is3aPremiumProductGroupSelector && defaultAdministrationFee) {
            tempData.push({
                fee: parseFloat(defaultAdministrationFee?.toFixed(2)?.replace(/\.?0*$/, '')),
                discountValue: 0,
                feeType: FEE_TYPES.administration,
                startDate: moment(),
            });
        }

        if (isZic) {
            tempData.push({ fee: defaultEntryFee, feeType: FEE_TYPES.entry });
        }

        return tempData;
    }, [isZivAzp, isZic, defaultCustodyFee, defaultAssetFee, defaultEntryFee, defaultAssetDiscount, is3aPremiumProductGroupSelector, defaultAdministrationFee]);

    const saveFeeLocally = (feeType, feeData) => {
        setFees((oldData) => {
            const tempData = [...oldData].filter((item) => item.feeType !== feeType);

            tempData.push(feeData);

            return tempData;
        });
    };

    useEffect(() => {
        const isStrategyChanged = JSON.parse(sessionStorage.getItem(STRATEGY_CHANGED));

        if (investmentApplicationId && defaultFees && isStrategyChanged) {
            setDefaultAssetManagementFeeInCNP({
                investmentApplicationId,
                fee: defaultFees?.find(fee => fee?.feeType === FEE_TYPES.asset),
            });
        }
    }, [investmentApplicationId, defaultFees]);

    useEffect(() => {
        setFees(currentInvestmentDescriptionSelector?.fees || defaultFees);
    }, [currentInvestmentDescriptionSelector?.fees, defaultFees]);

    const onSave = useCallback(async () => {
        try {
            const payload = {
                fees,
                investmentApplicationId,
            };

            if (payload.investmentApplicationId) {
                await saveInvestmentApplicationDataAction({
                    payload,
                    method: 'saveCustodyFee',
                });

                return fees;
            }
        } catch (err) {
            notification.open({
                content: `${err?.message}`,
                type: 'error',
            });
        }

        return undefined;
    }, [
        fees, dfsClientId, investmentApplicationId, saveInvestmentApplicationDataAction, defaultCustodyFee,
    ]);

    return {
        feeData: fees,
        onSave,
        defaultCustodyFee,
        defaultAssetFee,
        saveFeeLocally,
        defaultEntryFee,
        defaultAdministrationFee,
    };
};
